import React, { useEffect, useState } from "react";
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonItem,
  IonList,
  IonToolbar,
  CheckboxCustomEvent,
  IonInput,
  IonIcon,
  IonPage,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from "@ionic/react";
import { Item, SelectOptionProps } from "../../models/Data";
import { useTranslation } from "react-i18next";
import { closeCircleOutline, searchOutline } from "ionicons/icons";
import "./SelectOption.scss";

const CHUNK_SIZE = 20;

const SelectOption: React.FC<any> = (props: SelectOptionProps) => {
  const [filteredItems, setFilteredItems] = useState<Item[]>([]);
  const [displayedItems, setDisplayedItems] = useState<Item[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const { t } = useTranslation(["general"]);
  const [workingSelectedValues, setWorkingSelectedValues] = useState<string[]>(
    props.selectedItems
  );

  const isChecked = (value: any) => workingSelectedValues.includes(value);

  const resetPagination = (items: Item[]) => {
    setFilteredItems(items);
    setDisplayedItems(items.slice(0, CHUNK_SIZE));
    setCurrentIndex(CHUNK_SIZE);
  };

  const loadMoreItems = (ev?: CustomEvent<void>) => {
    const nextIndex = currentIndex + CHUNK_SIZE;
    const moreItems = filteredItems.slice(currentIndex, nextIndex);
    setDisplayedItems((prev) => [...prev, ...moreItems]);
    setCurrentIndex(nextIndex);
    if (ev) (ev.target as HTMLIonInfiniteScrollElement).complete();
  };

  const filterList = (searchQuery: string | null | undefined) => {
    let result = props.items;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((item) => item.text.toLowerCase().includes(query));
    }
    resetPagination(result);
  };

  const checkboxChange = (ev: CheckboxCustomEvent) => {
    const { checked, value } = ev.detail;
    if (props.multiple) {
      setWorkingSelectedValues((prev) =>
        checked ? [...prev, value] : prev.filter((item) => item !== value)
      );
    } else {
      setWorkingSelectedValues([value]);
      props.onSelectionChange(value);
    }
  };

  useEffect(() => {
    resetPagination(props.items);
  }, [props.items]);

  useEffect(() => {
    setWorkingSelectedValues([...props.selectedItems]);
  }, [props.selectedItems]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            onClick={props.onSelectionCancel}
          >
            <IonIcon slot="start" icon={closeCircleOutline} color="light" />
          </IonButton>
          <div className="search">
            <IonItem lines="none" className="item-select">
              <IonInput
                placeholder={`${t("search") ?? ""} ${props.title}`}
                className="input-select"
                onIonInput={(e: any) => filterList(e.detail.value!)}
              ></IonInput>
              <IonIcon icon={searchOutline} slot="start" />
            </IonItem>
          </div>
          <IonButton
            color="light"
            slot="end"
            fill="clear"
            onClick={() => props.onSelectionChange(workingSelectedValues)}
          >
            {t("submit")}
          </IonButton>
        </IonToolbar>
      </IonHeader>
      <IonContent color="light" className="select-option">
        <IonList id="modal-list" inset>
          {displayedItems.map((item, index) => (
            <IonItem key={index}>
              <IonCheckbox
                value={item.value}
                checked={isChecked(item.value)}
                onIonChange={checkboxChange}
              >
                {item.text}
              </IonCheckbox>
            </IonItem>
          ))}
        </IonList>

        <IonInfiniteScroll
          threshold="100px"
          disabled={currentIndex >= filteredItems.length}
          onIonInfinite={loadMoreItems}
        >
          <IonInfiniteScrollContent loadingText="Loading more..."></IonInfiniteScrollContent>
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default SelectOption;
